/**
 * The value of `release.multi-run-intro` (EN-10791): a JSON flag whose
 * variation IS the announcement. Each variation is one edition of the one-time
 * multi-run introduction — `nlhe-launch` at launch, `omaha-live` when Omaha
 * tables join — switched in LaunchDarkly with no deploy.
 *
 * Parsed here, at the flag boundary, so feature code only ever sees a
 * validated config or null:
 *   - An empty object is off: it is what the `off` variation serves, because
 *     LaunchDarkly refuses a null JSON variation and requires every variation
 *     to share a type. null (the SDK unavailable) and false are off too.
 *   - A payload without a valid `edition` is off, and is reported. The edition
 *     names the player's "seen" key, so there is nothing safe to fall back to.
 *   - Every other field is optional. A missing one falls back to the modal's
 *     timeless copy (components/MultiRunIntro/multiRunIntroCopy.ts); an invalid
 *     one falls back the same way and is reported. A typo in LaunchDarkly
 *     degrades to true, generic copy, never to a half-built modal or to stale
 *     "coming soon" text.
 *   - Lengths are capped so no field can break the portrait layout (see LIMITS).
 *   - Values are plain strings. React renders them as text: no HTML, no
 *     markdown, nothing to inject.
 * Unknown fields are ignored, so a variation can gain a field before the
 * client that reads it ships.
 */
export type MultiRunIntroConfig = {
  /** Names the seen key, the analytics events and the Sentry path. */
  edition: string;
  /** The headline as its two lines: the hook, then the answer. */
  titleLines?: readonly [string, string];
  /** One to three lines under the "How it works" card. */
  availability?: readonly string[];
  /** The fine print under the action — the only place "beta" may appear (AC-9.1). */
  smallPrint?: string;
};

export type MultiRunIntroConfigField = keyof MultiRunIntroConfig;

export type MultiRunIntroConfigIssue = { field: MultiRunIntroConfigField; reason: string };

/**
 * Lower-case letters, digits and dashes, starting with a letter or digit: safe
 * inside a storage key and readable in analytics. The LaunchDarkly variation
 * names follow it (`nlhe-launch`, `omaha-live`).
 */
export const MULTI_RUN_INTRO_EDITION_PATTERN = /^[a-z0-9][a-z0-9-]{0,39}$/;

/**
 * The longest text each field may carry, in characters. The launch copy sits
 * well inside them (headline lines of 21 and 16, availability lines of 54 and
 * 18, fine print of 182). Every field filled to its cap was checked in the
 * panel (2026-09-28): nothing clips or scrolls sideways at 375 or 390px, and
 * at 390×844 the panel still fits one screen. On a shorter phone (375×667) it
 * runs past the fold and the backdrop scrolls, with the close button in view.
 */
export const MULTI_RUN_INTRO_LIMITS = {
  titleLine: 28,
  availabilityLine: 80,
  availabilityLines: 3,
  smallPrint: 240
} as const;

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** A trimmed, non-empty string within `max`, or null. */
const readLine = (value: unknown, max: number): string | null => {
  if (typeof value !== 'string') return null;
  const line = value.trim();
  return line.length > 0 && line.length <= max ? line : null;
};

const readLines = (value: unknown, count: { min: number; max: number }, maxLength: number) => {
  if (!Array.isArray(value) || value.length < count.min || value.length > count.max) return null;
  const lines = value.map((line) => readLine(line, maxLength));
  return lines.every((line): line is string => line !== null) ? lines : null;
};

export const parseMultiRunIntroConfig = (
  raw: unknown,
  report: (issue: MultiRunIntroConfigIssue) => void = () => undefined
): MultiRunIntroConfig | null => {
  if (raw === null || raw === undefined || raw === false) return null;
  if (!isPlainObject(raw)) {
    report({ field: 'edition', reason: 'the payload is not an object' });
    return null;
  }
  // The `off` variation: nothing to announce, and not a mistake to report.
  if (Object.keys(raw).length === 0) return null;

  const edition = typeof raw.edition === 'string' ? raw.edition.trim() : '';
  if (!MULTI_RUN_INTRO_EDITION_PATTERN.test(edition)) {
    report({ field: 'edition', reason: 'missing, or not lower-case letters, digits and dashes' });
    return null;
  }

  let titleLines: [string, string] | undefined;
  if (raw.titleLines !== undefined) {
    const lines = readLines(raw.titleLines, { min: 2, max: 2 }, MULTI_RUN_INTRO_LIMITS.titleLine);
    if (lines) titleLines = [lines[0], lines[1]];
    else
      report({
        field: 'titleLines',
        reason: `expected exactly 2 lines of 1-${MULTI_RUN_INTRO_LIMITS.titleLine} characters`
      });
  }

  let availability: string[] | undefined;
  if (raw.availability !== undefined) {
    const lines = readLines(
      raw.availability,
      { min: 1, max: MULTI_RUN_INTRO_LIMITS.availabilityLines },
      MULTI_RUN_INTRO_LIMITS.availabilityLine
    );
    if (lines) availability = lines;
    else
      report({
        field: 'availability',
        reason: `expected 1-${MULTI_RUN_INTRO_LIMITS.availabilityLines} lines of 1-${MULTI_RUN_INTRO_LIMITS.availabilityLine} characters`
      });
  }

  let smallPrint: string | undefined;
  if (raw.smallPrint !== undefined) {
    const text = readLine(raw.smallPrint, MULTI_RUN_INTRO_LIMITS.smallPrint);
    if (text) smallPrint = text;
    else
      report({
        field: 'smallPrint',
        reason: `expected 1-${MULTI_RUN_INTRO_LIMITS.smallPrint} characters`
      });
  }

  return {
    edition,
    ...(titleLines ? { titleLines } : {}),
    ...(availability ? { availability } : {}),
    ...(smallPrint ? { smallPrint } : {})
  };
};
